import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useContent } from "../ContentContext";
import { priceOf, catSlugOf } from "../CatalogContext";
import { packByCode } from "../data/packs";
import HeartButton from "./HeartButton";

// Scroll to top on route change; scroll to the #anchor when there is one.
export function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        // Offset for the sticky header.
        const y = el.getBoundingClientRect().top + window.scrollY - 90;
        window.scrollTo({ top: y, behavior: "smooth" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

export function SectionHead({ tag, title, text }) {
  return (
    <div className="section-head">
      {tag ? <span className="section-tag">{tag}</span> : null}
      <h2>{title}</h2>
      {text ? <p>{text}</p> : null}
    </div>
  );
}

export function PageHero({ title, image }) {
  return (
    <div
      className="page-hero"
      style={image ? { backgroundImage: `url(${image})` } : undefined}
    >
      <div className="banner-overlay" />
      <div className="container">
        <h1>{title}</h1>
        <p className="crumb">
          <Link to="/">Home</Link> / {title}
        </p>
      </div>
    </div>
  );
}

export function HeroCarousel() {
  const { heroSlides = [] } = useContent();
  const [i, setI] = useState(0);
  const n = heroSlides.length;

  useEffect(() => {
    if (n < 2) return undefined;
    const t = setInterval(() => setI((p) => (p + 1) % n), 4500);
    return () => clearInterval(t);
  }, [n]);

  if (!n) return null;
  const idx = Math.min(i, n - 1);

  return (
    <div className="hero">
      <img src={heroSlides[idx]} alt={`Banner ${idx + 1}`} />
      {n > 1 ? (
        <>
          <button
            className="hero-arrow prev"
            onClick={() => setI((p) => (p - 1 + n) % n)}
            aria-label="Previous"
          >
            <i className="fas fa-angle-left" />
          </button>
          <button
            className="hero-arrow next"
            onClick={() => setI((p) => (p + 1) % n)}
            aria-label="Next"
          >
            <i className="fas fa-angle-right" />
          </button>
          <div className="hero-dots">
            {heroSlides.map((s, k) => (
              <button
                key={s || k}
                className={`hero-dot ${k === idx ? "active" : ""}`}
                onClick={() => setI(k)}
                aria-label={`Slide ${k + 1}`}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

// A real catalogue product: image, name + wishlist, price / MRP / discount.
// Both actions open the product page, where the shopper picks a pack size before
// adding — packs are priced individually, so we never guess one for them.
export function ProductCard({ product }) {
  const p = priceOf(product);
  const image = product.images?.[0] || "";
  const to = `/product/${product.slug}`;

  return (
    <div className="product-box">
      <Link className="product-media" to={to}>
        {image ? (
          <img src={image} alt={product.title} loading="lazy" />
        ) : (
          <div className="product-noimg" />
        )}
      </Link>

      {/* Name with the wishlist heart sitting right beside it */}
      <div className="product-title-row">
        <Link to={to}>
          <h4>{product.title}</h4>
        </Link>
        <HeartButton productId={product._id} className="inline" />
      </div>

      {product.code ? (
        <span className="product-code">{product.code}</span>
      ) : null}

      {p ? (
        <div className="price-row">
          <span className="price-now">₹{p.price.toLocaleString("en-IN")}</span>
          {p.mrp ? (
            <span className="price-mrp">₹{p.mrp.toLocaleString("en-IN")}</span>
          ) : null}
          {p.off > 0 ? <span className="price-off">{p.off}% OFF</span> : null}
        </div>
      ) : null}

      <div className="product-actions">
        <Link className="product-btn cart" to={to}>
          <i className="fas fa-cart-plus" /> Add to Cart
        </Link>
        <Link className="product-btn buy" to={`${to}?buy=1`}>
          <i className="fas fa-bolt" /> Buy Now
        </Link>
      </div>
    </div>
  );
}

// Showcase pack card — image + name + View Details, as on the source site.
// No price: these are catalogue packs, not the purchasable admin products.
export function PackCard({ brand, category, code }) {
  const { productImg } = useContent();
  const pack = packByCode(code);
  return (
    <div className="product-box">
      <Link className="product-media" to={`/pack/${code}`}>
        <img
          src={productImg(brand, category, code)}
          alt={pack?.name || code}
          loading="lazy"
        />
      </Link>
      {/* <h4>{pack?.name || code}</h4> */}
      <Link className="product-btn pack-btn" to={`/pack/${code}`}>
        View Details
      </Link>
    </div>
  );
}

export function PackGrid({ brand, category, codes = [] }) {
  return (
    <div className="product-grid">
      {codes.map((c) => (
        <PackCard
          key={`${brand.slug}-${category}-${c}`}
          brand={brand}
          category={category}
          code={c}
        />
      ))}
    </div>
  );
}

// Horizontal slider of showcase packs (home brand sections).
const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const STEP = 270 + 26; // one card + gap

export function PackSlider({ brand, category, codes = [] }) {
  const ref = useRef(null);
  const pausedRef = useRef(false);
  const resumeTimer = useRef(null);
  // When the row is longer than the viewport we render the cards twice and loop
  // seamlessly; short rows that already fit stay static.
  const [loop, setLoop] = useState(false);

  const scrollBy = (dir) => ref.current?.scrollBy({ left: dir * STEP * 2, behavior: "smooth" });

  // Decide whether to loop: only when a single set overflows the container.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setLoop(el.scrollWidth > el.clientWidth + 4 && codes.length > 1 && !prefersReducedMotion());
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
    // Re-measure whenever the single-copy width could change.
  }, [codes.length, loop]);

  // Seamless auto-scroll: advance one card, and once a full set has passed,
  // instantly rewind by that set's width. Because the cards are duplicated, the
  // rewind lands on identical pixels — so it looks like product 1 simply follows
  // the last product, with no jump-back.
  useEffect(() => {
    const el = ref.current;
    if (!el || !loop) return undefined;

    // Exact width of one copy = where the first card of the 2nd copy starts.
    const setWidth = () => {
      const kids = el.children;
      if (kids.length <= codes.length) return 0;
      return kids[codes.length].offsetLeft - kids[0].offsetLeft;
    };

    const id = setInterval(() => {
      if (pausedRef.current) return;
      const w = setWidth();
      if (w && el.scrollLeft >= w) el.scrollLeft -= w; // invisible rewind
      el.scrollTo({ left: el.scrollLeft + STEP, behavior: "smooth" });
    }, 2600);
    return () => clearInterval(id);
  }, [loop, codes.length]);

  const pause = () => {
    clearTimeout(resumeTimer.current);
    pausedRef.current = true;
  };
  // Resume shortly after the interaction ends so a manual swipe isn't fought.
  const resume = (delay = 0) => {
    clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => { pausedRef.current = false; }, delay);
  };

  if (!codes.length) return null;

  const rendered = loop ? [...codes, ...codes] : codes;

  return (
    <div
      className="slider-wrap"
      onMouseEnter={pause}
      onMouseLeave={() => resume(0)}
      onTouchStart={pause}
      onTouchEnd={() => resume(4000)}
    >
      <button
        className="slider-arrow prev"
        onClick={() => scrollBy(-1)}
        aria-label="Previous"
      >
        <i className="fas fa-angle-left" />
      </button>
      <div className="product-slider" ref={ref}>
        {rendered.map((c, i) => (
          <PackCard
            key={`${brand.slug}-${category}-${c}-${i}`}
            brand={brand}
            category={category}
            code={c}
          />
        ))}
      </div>
      <button
        className="slider-arrow next"
        onClick={() => scrollBy(1)}
        aria-label="Next"
      >
        <i className="fas fa-angle-right" />
      </button>
    </div>
  );
}

// Discount % off MRP for a product.
export const discountOf = (p) =>
  p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;

// Discount thresholds offered in the filter.
export const DISCOUNT_TIERS = [10, 20, 30, 40, 50];

// Pack size + price sort + admin-marked flags + discount filtering.
export function useProductFilters(products = [], categories = [], brands = []) {
  const [size, setSize] = useState("all");
  const [sort, setSort] = useState("featured");
  const [flag, setFlag] = useState("all"); // 'all' | 'new' | 'best'
  const [minOff, setMinOff] = useState(0); // 0 = any discount
  const [cat, setCat] = useState("all"); // category slug or 'all'
  const [brand, setBrand] = useState("all"); // brand name or 'all'

  // Brands to offer: the Website Content brands that actually have products
  // (matched by name). Falls back to the brand names found on the products.
  const brandOpts = useMemo(() => {
    const present = new Set(products.map((p) => p.brand).filter(Boolean));
    const known = (brands || []).filter((b) => present.has(b.name)).map((b) => b.name);
    const names = known.length ? known : [...present];
    return names;
  }, [products, brands]);

  const sizes = useMemo(() => {
    const s = new Set();
    products.forEach((p) =>
      (p.variants || []).forEach((v) => v.weight && s.add(v.weight))
    );
    // Order by the numeric part of the weight (1kg, 5kg, 25kg…).
    return [...s].sort((a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0));
  }, [products]);

  // Categories to offer: those that actually have products. Fall back to the
  // slugs found on the products when no category list is supplied.
  const cats = useMemo(() => {
    const present = new Set(products.map(catSlugOf).filter(Boolean));
    const known = (categories || []).filter((c) => present.has(c.slug));
    if (known.length) return known;
    return [...present].map((slug) => ({ slug, name: slug }));
  }, [products, categories]);

  const filtered = useMemo(() => {
    let list = products;

    if (brand !== "all") list = list.filter((p) => (p.brand || "") === brand);
    if (cat !== "all") list = list.filter((p) => catSlugOf(p) === cat);
    if (size !== "all")
      list = list.filter((p) =>
        (p.variants || []).some((v) => v.weight === size)
      );
    // New Arrivals / Best Selling are whatever the admin has marked.
    if (flag === "new") list = list.filter((p) => p.isNewArrival);
    else if (flag === "best") list = list.filter((p) => p.isBestSeller);
    if (minOff > 0) list = list.filter((p) => discountOf(p) >= minOff);

    if (sort === "low")
      list = [...list].sort((a, b) => (a.price || 0) - (b.price || 0));
    else if (sort === "high")
      list = [...list].sort((a, b) => (b.price || 0) - (a.price || 0));
    else if (sort === "discount")
      list = [...list].sort((a, b) => discountOf(b) - discountOf(a));

    return list;
  }, [products, brand, cat, size, sort, flag, minOff]);

  const reset = () => {
    setBrand("all");
    setCat("all");
    setSize("all");
    setSort("featured");
    setFlag("all");
    setMinOff(0);
  };
  const active =
    brand !== "all" || cat !== "all" || size !== "all" || sort !== "featured" || flag !== "all" || minOff > 0;

  return {
    brandOpts,
    brand,
    setBrand,
    cats,
    cat,
    setCat,
    sizes,
    size,
    setSize,
    sort,
    setSort,
    flag,
    setFlag,
    minOff,
    setMinOff,
    filtered,
    reset,
    active,
  };
}

export function ProductFilters({
  brandOpts = [],
  brand,
  setBrand,
  cats = [],
  cat,
  setCat,
  sizes,
  size,
  setSize,
  sort,
  setSort,
  flag,
  setFlag,
  minOff,
  setMinOff,
  count,
  reset,
  active,
}) {
  const [open, setOpen] = useState(false);
  // How many filters are on — badged on the Filter button.
  const activeCount =
    (brand !== "all" ? 1 : 0) +
    (cat !== "all" ? 1 : 0) +
    (size !== "all" ? 1 : 0) +
    (flag !== "all" ? 1 : 0) +
    (minOff > 0 ? 1 : 0) +
    (sort !== "featured" ? 1 : 0);

  return (
    <>
      {/* Filter toggle + result count */}
      <div className="filter-toolbar">
        <button
          className={`filter-toggle ${open ? "open" : ""} ${
            activeCount ? "has" : ""
          }`}
          onClick={() => setOpen((o) => !o)}
        >
          <i className="fas fa-sliders" /> Filters
          {activeCount ? (
            <span className="filter-badge">{activeCount}</span>
          ) : null}
          <i className={`fas fa-angle-${open ? "up" : "down"}`} />
        </button>

        <div className="filter-sort">
          <span className="filter-count">
            {count} item{count === 1 ? "" : "s"}
          </span>
          {active ? (
            <button className="filter-clear" onClick={reset}>
              Clear
            </button>
          ) : null}
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            aria-label="Sort products"
          >
            <option value="featured">Sort: Featured</option>
            <option value="low">Price: Low to High</option>
            <option value="high">Price: High to Low</option>
            <option value="discount">Discount: High to Low</option>
          </select>
        </div>
      </div>

      {/* The panel drops open below the toolbar */}
      <div className={`filter-panel ${open ? "open" : ""}`}>
        <div className="filter-groups">
          {/* Brand */}
          {brandOpts.length ? (
            <div className="filter-sizes">
              <span className="filter-label">
                <i className="fas fa-crown" /> Brand
              </span>
              <button
                className={`filter-chip ${brand === "all" ? "active" : ""}`}
                onClick={() => setBrand("all")}
              >
                All
              </button>
              {brandOpts.map((b) => (
                <button
                  key={b}
                  className={`filter-chip ${brand === b ? "active" : ""}`}
                  onClick={() => setBrand(b)}
                >
                  {b}
                </button>
              ))}
            </div>
          ) : null}

          {/* Category */}
          {cats.length ? (
            <div className="filter-sizes">
              <span className="filter-label">
                <i className="fas fa-layer-group" /> Category
              </span>
              <button
                className={`filter-chip ${cat === "all" ? "active" : ""}`}
                onClick={() => setCat("all")}
              >
                All
              </button>
              {cats.map((c) => (
                <button
                  key={c.slug}
                  className={`filter-chip ${cat === c.slug ? "active" : ""}`}
                  onClick={() => setCat(c.slug)}
                >
                  {c.name}
                </button>
              ))}
            </div>
          ) : null}

          {/* Admin-marked collections */}
          <div className="filter-sizes">
            <span className="filter-label">
              <i className="fas fa-filter" /> Show
            </span>
            <button
              className={`filter-chip ${flag === "all" ? "active" : ""}`}
              onClick={() => setFlag("all")}
            >
              All
            </button>
            <button
              className={`filter-chip ${flag === "new" ? "active" : ""}`}
              onClick={() => setFlag("new")}
            >
              <i className="fas fa-sparkles" /> New Arrivals
            </button>
            <button
              className={`filter-chip ${flag === "best" ? "active" : ""}`}
              onClick={() => setFlag("best")}
            >
              <i className="fas fa-fire" /> Best Selling
            </button>
          </div>

          {sizes.length ? (
            <div className="filter-sizes">
              <span className="filter-label">
                <i className="fas fa-box" /> Pack size
              </span>
              <button
                className={`filter-chip ${size === "all" ? "active" : ""}`}
                onClick={() => setSize("all")}
              >
                All
              </button>
              {sizes.map((s) => (
                <button
                  key={s}
                  className={`filter-chip ${size === s ? "active" : ""}`}
                  onClick={() => setSize(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          ) : null}

          {/* Discount tiers */}
          <div className="filter-sizes">
            <span className="filter-label">
              <i className="fas fa-tag" /> Discount
            </span>
            <button
              className={`filter-chip ${minOff === 0 ? "active" : ""}`}
              onClick={() => setMinOff(0)}
            >
              Any
            </button>
            {DISCOUNT_TIERS.map((d) => (
              <button
                key={d}
                className={`filter-chip ${minOff === d ? "active" : ""}`}
                onClick={() => setMinOff(d)}
              >
                {d}% &amp; above
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// Full grid — every product visible (brand / category pages).
export function ProductGrid({ products = [] }) {
  return (
    <div className="product-grid">
      {products.map((p) => (
        <ProductCard key={p._id || p.slug} product={p} />
      ))}
    </div>
  );
}

// Horizontal slider with left/right arrows — the home product rows.
export function ProductSlider({ products = [] }) {
  const ref = useRef(null);

  const scrollBy = (dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * (270 + 26) * 2, behavior: "smooth" });
  };

  if (!products.length) return null;

  return (
    <div className="slider-wrap">
      <button
        className="slider-arrow prev"
        onClick={() => scrollBy(-1)}
        aria-label="Previous products"
      >
        <i className="fas fa-angle-left" />
      </button>

      <div className="product-slider" ref={ref}>
        {products.map((p) => (
          <ProductCard key={p._id || p.slug} product={p} />
        ))}
      </div>

      <button
        className="slider-arrow next"
        onClick={() => scrollBy(1)}
        aria-label="Next products"
      >
        <i className="fas fa-angle-right" />
      </button>
    </div>
  );
}

// Image + text feature block, alternating sides down the page.
// Centred heading + full-width image on the grey band. This is how the site
// presents Why Choose Us, the quality blocks, the market block and the story
// blocks — they are not side-by-side splits.
export function FeatureBlock({ tag, title, text, image }) {
  return (
    <section className="feature-section">
      <div className="container">
        <SectionHead tag={tag} title={title} text={text} />
        {image ? (
          <a
            className="iso-image"
            href={image}
            target="_blank"
            rel="noreferrer"
          >
            <img src={image} alt={title} loading="lazy" />
          </a>
        ) : null}
      </div>
    </section>
  );
}

export function Split({ tag, title, text, image, reverse, cta }) {
  return (
    <div className={`split ${reverse ? "reverse" : ""}`}>
      <div className="split-media">
        <img src={image} alt={title} loading="lazy" />
      </div>
      <div>
        {tag ? <span className="section-tag">{tag}</span> : null}
        <h2>{title}</h2>
        <p>{text}</p>
        {cta ? (
          <Link className="btn-gold" to={cta.to}>
            {cta.label}
          </Link>
        ) : null}
      </div>
    </div>
  );
}

// Horizontal card: fixed 180×140 image on the left, copy on the right.
export function FestivalCard({ title, text, image }) {
  return (
    <div className="festival-card">
      <div className="festival-image">
        <img src={image} alt={title} loading="lazy" />
      </div>
      <div className="festival-content">
        <h3>{title}</h3>
        <p>{text}</p>
      </div>
    </div>
  );
}

export function InfoCard({ title, text, image }) {
  return (
    <div className="info-card">
      <img src={image} alt={title} loading="lazy" />
      <div className="body">
        <h3>{title}</h3>
        <p>{text}</p>
      </div>
    </div>
  );
}
